import { supabase } from '../../config/supabase';
import { Database } from '@travel-dashboard/shared';

export type SupabaseUser = Database['travel']['Tables']['users']['Row'];

export interface CreateUserInput {
  id: string; // Firebase UID
  email: string;
  display_name?: string | null;
  photo_url?: string | null;
  preferred_currency?: string;
}

export interface UpdateUserInput {
  display_name?: string | null;
  photo_url?: string | null;
  preferred_currency?: string;
}

export const usersService = {
  /**
   * Get a user by Firebase UID
   */
  async getUserById(userId: string): Promise<SupabaseUser | null> {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // User not found
        return null;
      }
      console.error('Error fetching user:', error);
      throw error;
    }

    return data;
  },

  /**
   * Create or update a user (upsert)
   * This should be called when a user signs in with Firebase
   */
  async upsertUser(input: CreateUserInput): Promise<SupabaseUser> {
    const { data, error } = await supabase
      .from('users')
      .upsert(input, {
        onConflict: 'id',
      })
      .select()
      .single();

    if (error) {
      console.error('Error upserting user:', error);
      throw error;
    }

    return data;
  },

  /**
   * Update user profile
   */
  async updateUser(userId: string, input: UpdateUserInput): Promise<SupabaseUser> {
    const { data, error } = await supabase
      .from('users')
      .update(input)
      .eq('id', userId)
      .select()
      .single();

    if (error) {
      console.error('Error updating user:', error);
      throw error;
    }

    return data;
  },
};
